import { makeStyles } from '@material-ui/core/styles';
import { EntityComponentTierFilter, CustomFilters } from '../CustomFilters';
import { useEntityList } from '@backstage/plugin-catalog-react';
import {
  Select,
  MenuItem,
  FormControl,
  Chip,
  Box,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@material-ui/core';

/** @public */
export type CatalogReactEntityComponentTierPickerClassKey = 'input';

const useStyles = makeStyles(
  theme => ({
    input: {
      backgroundColor: theme.palette.background.paper,
    },
  }),
  { name: 'CatalogReactEntityComponentTierPicker' },
);

/** @public */
export const EntityComponentTierPicker = () => {
  const classes = useStyles();

  const {
    filters: { componentTier },
    updateFilters,
  } = useEntityList<CustomFilters>();

  // All tier options - always show all available tiers regardless of current data
  const tierOptions = [
    { value: '0', label: 'Tier 0 - Highly critical' },
    { value: '1', label: 'Tier 1 - Critical' },
    { value: '2', label: 'Tier 2 - Important' },
    { value: '3', label: 'Tier 3 - Internal' },
    { value: '4', label: 'Tier 4 - Ancillary' },
    { value: 'none', label: 'Not a Service' },
    { value: '__missing__', label: 'No Tier Assigned' },
  ];

  // Always show all tier options regardless of current data
  // The options are already set in the initial state and don't need to be updated

  function onChange(event: React.ChangeEvent<{ value: unknown }>) {
    const values = event.target.value as string[];
    updateFilters({
      componentTier: values.length
        ? new EntityComponentTierFilter(values)
        : undefined,
    });
  }

  const selectedValues = componentTier?.values || [];

  return (
    <div>
      <label
        style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}
        htmlFor="component-tier-picker"
      >
        Component Tier
      </label>
      <FormControl
        id="component-tier-picker"
        variant="outlined"
        size="small"
        fullWidth
        className={classes.input}
      >
        <Select
          multiple
          value={selectedValues}
          onChange={onChange}
          input={<OutlinedInput />}
          renderValue={selected => (
            <Box display="flex" flexWrap="wrap" style={{ gap: '4px' }}>
              {(selected as string[]).map(value => {
                const option = tierOptions.find(opt => opt.value === value);
                return (
                  <Chip
                    key={value}
                    label={option?.label || value}
                    size="small"
                    onDelete={() => {
                      const newValues = selectedValues.filter(v => v !== value);
                      updateFilters({
                        componentTier: newValues.length
                          ? new EntityComponentTierFilter(newValues)
                          : undefined,
                      });
                    }}
                  />
                );
              })}
            </Box>
          )}
        >
          {tierOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox checked={selectedValues.indexOf(option.value) > -1} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};
